import { useEffect, useRef, useState } from 'react';
import type { SseOrderEvent } from '../../shared/types';
import { apiRequest, apiUrl, jsonBody } from '../lib/api';

const EVENT_NAMES = [
  'order.created',
  'order.modified',
  'order.status',
  'order.item.preparation',
  'order.completed',
  'fulfillment.location',
  'fulfillment.token',
  'fulfillment.ready',
  'fulfillment.transit',
  'fulfillment.delivered',
  'fulfillment.handed_off',
  'fulfillment.cancelled',
  'correlation.reset',
] as const;

export function useSse(branchId: string | null | undefined, onEvent: (event: SseOrderEvent) => void) {
  const [status, setStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const callbackRef = useRef(onEvent);
  const lastIdRef = useRef(0);
  callbackRef.current = onEvent;

  useEffect(() => {
    if (!branchId) {
      setStatus('idle');
      return;
    }

    let source: EventSource | null = null;
    let cancelled = false;
    let retryTimer: number | null = null;

    const closeSource = () => {
      const current = source;
      source = null;
      current?.close();
    };

    const scheduleReconnect = (delayMs: number, markAsError = true) => {
      if (cancelled || retryTimer !== null) return;
      closeSource();
      setStatus(markAsError ? 'error' : 'connecting');
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        void connect();
      }, delayMs);
    };

    const connect = async () => {
      if (cancelled) return;
      setStatus('connecting');
      try {
        const ticket = await apiRequest<{ token: string }>('/api/auth/sse-token', {
          method: 'POST',
          body: jsonBody({ branchId }),
        });
        if (cancelled) return;

        const url = apiUrl(`/api/events/branches/${encodeURIComponent(branchId)}?token=${encodeURIComponent(ticket.token)}&lastEventId=${lastIdRef.current}`);
        const nextSource = new EventSource(url);
        source = nextSource;

        nextSource.addEventListener('ready', () => {
          if (source === nextSource) setStatus('connected');
        });
        for (const name of EVENT_NAMES) {
          nextSource.addEventListener(name, (raw) => {
            if (source !== nextSource) return;
            const message = raw as MessageEvent<string>;
            try {
              const event = JSON.parse(message.data) as SseOrderEvent;
              lastIdRef.current = Math.max(lastIdRef.current, Number(message.lastEventId || event.id || 0));
              callbackRef.current(event);
            } catch {
              // El siguiente evento válido mantendrá la conexión actualizada.
            }
          });
        }
        nextSource.addEventListener('reconnect', () => {
          if (source === nextSource) scheduleReconnect(500, false);
        });
        nextSource.onerror = () => {
          if (source === nextSource) scheduleReconnect(2_000);
        };
      } catch {
        scheduleReconnect(3_000);
      }
    };

    void connect();
    return () => {
      cancelled = true;
      closeSource();
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [branchId]);

  return status;
}
